import React, { useState, useEffect } from 'react';

/**
 * Mobile Landing Page Component - Messaging App Style
 * Similar to WhatsApp/iMessage chat list
 */
const MobileLandingPage = ({ 
  narratives, 
  user, 
  onNarrativeSelect, 
  onSignIn, 
  onSignOut,
  loadingNarratives 
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter narratives based on search
  const filteredNarratives = narratives.filter(narrative => {
    const genreText = Array.isArray(narrative.genres)
      ? narrative.genres.join(' ')
      : (narrative.genre || '');
    return (
      narrative.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (narrative.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      genreText.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Get icon for narrative based on genre
  const getGenreIcon = (genre) => {
    const icons = {
      'Adventure': '🗺️',
      'Mystery': '🔍', 
      'Fantasy': '⚔️',
      'Sci-Fi': '🚀',
      'Horror': '👻',
      'Romance': '💕',
      'Western': '🤠',
      'Thriller': '🎭',
      'Comedy': '😄'
    };
    return icons[genre] || '📖';
  };

  // Get message count for narrative - Always show "Available" for now
  const getMessageCount = (narrative) => {
    // Since current_participants field is stale, show generic "Available"
    // User will get accurate info when they click and see room selection modal
    return "Available";
  };

  // Get participant info for narrative - Always show "Explore story" for now
  const getParticipantInfo = (narrative) => {
    // Since current_participants field is stale, show generic message
    // User will get accurate info when they click and see room selection modal
    return "Explore this story";
  };

  if (loadingNarratives) {
    return (
      <div className="h-screen bg-gray-900 flex flex-col">
        {/* Mobile Header */}
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-white">PlayScreen</h1>
          {user ? (
            <button
              onClick={onSignOut}
              className="text-blue-400 text-sm font-medium"
            >
              Sign Out
            </button>
          ) : (
            <button
              onClick={onSignIn}
              className="text-blue-400 text-sm font-medium"
            >
              Sign In
            </button>
          )}
        </div>

        {/* Loading State */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading stories...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      {/* Mobile Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <h1 className="text-xl font-semibold text-white">PlayScreen</h1>
        {user ? (
          <button
            onClick={onSignOut}
            className="text-blue-400 text-sm font-medium"
          >
            Sign Out
          </button>
        ) : (
          <button
            onClick={onSignIn}
            className="text-blue-400 text-sm font-medium"
          >
            Sign In
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex-shrink-0">
        <div className="relative">
          <input
            type="text"
            placeholder="Search stories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-700 text-white placeholder-gray-400 rounded-lg px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* User Status */}
      {user && (
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-2 flex-shrink-0">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            <span className="text-sm text-gray-300">Signed in as {user.email}</span>
          </div>
        </div>
      )}

      {/* Stories List */}
      <div className="flex-1 overflow-y-auto">
        {filteredNarratives.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center text-gray-400">
              <p className="text-lg mb-2">No stories found</p>
              {searchTerm && (
                <p className="text-sm">Try a different search term</p>
              )}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {filteredNarratives.map((narrative) => (
              <div
                key={narrative.id}
                onClick={() => onNarrativeSelect(narrative)}
                className="flex items-center px-4 py-4 bg-gray-900 hover:bg-gray-800 active:bg-gray-700 transition-colors cursor-pointer"
              >
                {/* Thumbnail Image */}
                <div className="flex-shrink-0 mr-3">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-700">
                    <img
                      src={narrative.image_url}
                      alt={narrative.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to emoji if image fails to load
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-gray-700 rounded-lg hidden items-center justify-center text-xl">
                      {getGenreIcon(Array.isArray(narrative.genres) ? narrative.genres[0] : narrative.genre)}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-white font-medium text-base truncate pr-2">
                      {narrative.title}
                    </h3>
                    <div className="flex items-center flex-shrink-0">
                      <span className="text-xs text-gray-400 mr-2">
                        {/* Removed generic activity indicator */}
                      </span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-400 truncate pr-2">
                      {narrative.description}
                    </p>
                  </div>
                  
                  {/* Participant info */}
                  <div className="mt-1 text-xs text-gray-500">
                    {/* Removed generic participant info */}
                  </div>
                  
                  {/* Genre tags */}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(Array.isArray(narrative.genres) ? narrative.genres : [narrative.genre]).filter(Boolean).map(g => (
                      <span key={g} className="inline-block bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded-full">
                        {g}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Status */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-2 flex-shrink-0">
        <div className="text-center text-xs text-gray-400">
          {filteredNarratives.length} {filteredNarratives.length === 1 ? 'story' : 'stories'} available
        </div>
      </div>
    </div>
  );
};

export default MobileLandingPage;